import React, { useEffect, useState } from 'react';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAuth } from '../../../auth/AuthProvider';
import { useAsyncData } from '../hooks/useAsyncData';
import { ConfirmDialog, ErrorState, FeedbackBanner, FieldGroup, LoadingState, MvpCard, MvpTalentLayout } from '../components/MvpTalentUi';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState(true);
  const [draftMode, setDraftMode] = useState<'enabled' | 'read_only' | 'disabled'>('read_only');
  const { data, loading, error, reload, setData } = useAsyncData((signal) => platformCoreNetworkApi.getPlatformSettings({ signal }), []);

  useEffect(() => {
    if (data) {
      setDraftEnabled(Boolean(data.coreNetworkGlobalEnabled));
      setDraftMode(data.portalAccessMode);
    }
  }, [data]);

  return (
    <MvpTalentLayout title="Platform Settings" subtitle="Global Core Network controls and emergency behavior. Sensitive changes require confirmation and a reason.">
      {feedback ? <FeedbackBanner tone={feedback.tone} message={feedback.message} onDismiss={() => setFeedback(null)} /> : null}
      {loading ? <LoadingState label="Loading platform settings..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <>
          <MvpCard title="Emergency controls">
            <div className="mvp-filter-grid">
              <FieldGroup label="Global Core Network enabled">
                <select value={draftEnabled ? 'true' : 'false'} onChange={(event) => setDraftEnabled(event.target.value === 'true')}>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </FieldGroup>
              <FieldGroup label="Portal access mode">
                <select value={draftMode} onChange={(event) => setDraftMode(event.target.value as 'enabled' | 'read_only' | 'disabled')}>
                  <option value="enabled">Enabled</option>
                  <option value="read_only">Read-only</option>
                  <option value="disabled">Disabled</option>
                </select>
              </FieldGroup>
            </div>
            <p>
              Global disable blocks new Core Network requests, offers, and assignments. It does not disable Binated, internal artwork requests, or historical data visibility.
            </p>
            <button type="button" className="mvp-button" onClick={() => setDialogOpen(true)}>
              Save platform settings
            </button>
          </MvpCard>

          <MvpCard title="Planned extension points">
            <p>Default offer expiry, matching limits, notification defaults, and support contact settings remain backend-dependent in this initial release. This screen preserves room for those controls without duplicating backend business rules.</p>
            <p>Signed in as: <strong>{user?.email}</strong></p>
          </MvpCard>
        </>
      ) : null}
      <ConfirmDialog
        open={dialogOpen}
        title="Apply global Core Network settings"
        description="This is a sensitive platform-wide action. Review the impact carefully before confirming."
        confirmLabel="Apply settings"
        confirmTone={!draftEnabled ? 'danger' : 'default'}
        onClose={() => setDialogOpen(false)}
        onConfirm={async ({ reason }) => {
          try {
            const updated = await platformCoreNetworkApi.updatePlatformSettings({ coreNetworkGlobalEnabled: draftEnabled, portalAccessMode: draftMode, reason });
            setData(updated);
            setFeedback({ tone: 'success', message: 'Platform settings updated.' });
            await reload();
          } catch (settingsError: any) {
            setFeedback({ tone: 'error', message: settingsError?.message || 'Unable to update platform settings.' });
            throw settingsError;
          }
        }}
      />
    </MvpTalentLayout>
  );
};

export default SettingsPage;
